import React, { useState } from 'react';
import { FiX, FiDownload, FiUpload, FiAlertCircle, FiCheckCircle, FiFile, FiHelpCircle } from 'react-icons/fi';
import { motion, AnimatePresence } from 'framer-motion';
import api from '../../../api';

const BulkUploadModal = ({
  isOpen,
  onClose,
  onSuccess,
  classItem,
  subject,
  labBatch,
  facultyId,
  type,
  templateEndpoint,
  uploadEndpoint
}) => {
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [uploadErrors, setUploadErrors] = useState([]);

  const handleFileSelect = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      // Validate file type
      const validTypes = [
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        'application/vnd.ms-excel'
      ];
      const fileExt = selectedFile.name.split('.').pop().toLowerCase();
      
      if (!validTypes.includes(selectedFile.type) && !['xlsx', 'xls'].includes(fileExt)) {
        setError('Please upload a valid Excel file (.xlsx or .xls)');
        setFile(null);
        return;
      }
      
      setFile(selectedFile);
      setError(null);
      setResult(null);
      setUploadErrors([]);
    }
  };

  const downloadTemplate = async () => {
    try {
      setError(null);
      const response = await api.get(templateEndpoint, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${type}_template.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading template:', error);
      setError('Failed to download template. Please try again.');
    }
  };

  const handleUpload = async () => {
    if (!file) {
      setError('Please select a file');
      return;
    }

    setLoading(true);
    setProgress(0);
    setError(null);
    setResult(null);
    setUploadErrors([]);

    const formData = new FormData();
    formData.append('file', file);
    formData.append('classId', classItem?.classId || classItem?._id);
    formData.append('subjectId', subject?.subjectId || subject?._id);
    if (labBatch?.batchId) {
      formData.append('labBatchId', labBatch.batchId);
    }

    try {
      // Log the request for debugging
      console.log('[BulkUploadModal] Uploading to:', uploadEndpoint);
      console.log('[BulkUploadModal] Form data:', {
        classId: classItem?.classId || classItem?._id,
        subjectId: subject?.subjectId || subject?._id,
        labBatchId: labBatch?.batchId || null,
        fileName: file.name,
        fileSize: file.size
      });

      const response = await api.post(uploadEndpoint, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        },
        onUploadProgress: (progressEvent) => {
          const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setProgress(percentCompleted);
        }
      });

      console.log('[BulkUploadModal] Response:', response.data);

      if (response.data?.success) {
        setResult(response.data.data);
        // Show detailed results
        if (response.data.data?.failed?.length > 0) {
          const errors = response.data.data.failed.map(f => `Row ${f.row}: ${f.error}`);
          setUploadErrors(errors);
        }
        setTimeout(() => {
          onSuccess();
          onClose();
        }, 3000);
      } else {
        setError(response.data?.message || 'Upload failed');
      }
    } catch (error) {
      console.error('[BulkUploadModal] Upload error:', error);
      console.error('[BulkUploadModal] Error response:', error.response?.data);
      
      // Handle validation errors from backend
      if (error.response?.data?.errors) {
        const errors = error.response.data.errors.map(e => `Row ${e.row}: ${e.error}`);
        setUploadErrors(errors);
        setError(error.response.data.message || 'Validation errors found');
      } else {
        setError(error.response?.data?.message || error.message || 'Upload failed. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  // Responsive device variant handlers for wrapper positioning
  const modalVariants = {
    hidden: { 
      y: window.innerWidth < 640 ? '100%' : 15, 
      scale: window.innerWidth < 640 ? 1 : 0.96,
      opacity: window.innerWidth < 640 ? 1 : 0 
    },
    visible: { 
      y: 0, 
      scale: 1, 
      opacity: 1,
      transition: { type: 'spring', damping: 25, stiffness: 350 }
    },
    exit: { 
      y: window.innerWidth < 640 ? '100%' : 10, 
      scale: window.innerWidth < 640 ? 1 : 0.96,
      opacity: window.innerWidth < 640 ? 1 : 0,
      transition: { duration: 0.15 }
    }
  };

  return (
    <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm z-50 flex items-end sm:items-center justify-center">
      <AnimatePresence>
        <motion.div
          variants={modalVariants}
          initial="hidden"
          animate="visible"
          exit="exit"
          className="bg-white rounded-t-2xl sm:rounded-2xl max-w-lg w-full max-h-[88vh] sm:max-h-[90vh] flex flex-col overflow-hidden shadow-xl border border-slate-100"
        >
          {/* Top Notch Bar Indicator for Mobile Drag UX feel */}
          <div className="w-full flex justify-center py-2 sm:hidden flex-shrink-0 bg-slate-50 border-b border-slate-100">
            <div className="w-12 h-1 bg-slate-300 rounded-full" />
          </div>

          <div className="p-5 sm:p-6 overflow-y-auto space-y-5 flex-1">
            {/* Header Title area */}
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-base font-bold text-slate-800 capitalize">
                  Excel Bulk Upload ({type.replace('-', ' ')})
                </h3>
                <p className="text-xs text-slate-400 font-medium mt-0.5">Add multiple records using a spreadsheet</p>
              </div>
              <button
                onClick={onClose}
                className="w-8 h-8 flex items-center justify-center text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
              >
                <FiX className="w-5 h-5" />
              </button>
            </div>

            {/* Steps & Instructions banner */}
            <div className="p-4 bg-slate-50 rounded-xl border border-slate-200/60 space-y-2">
              <h4 className="text-xs font-bold text-slate-700 tracking-wide uppercase flex items-center gap-1.5">
                <FiHelpCircle className="w-3.5 h-3.5 text-indigo-500" />
                Instructions & Setup Steps
              </h4>
              <ol className="text-xs text-slate-600 space-y-1 list-decimal list-inside pl-0.5 leading-relaxed font-medium">
                <li>Download the sample layout template down below.</li>
                <li>Fill in the exact columns inside the sheet.</li>
                <li>Save the completed document as an <strong className="text-indigo-600">.xlsx</strong> file.</li>
                <li>Choose or drop the document here to upload.</li>
              </ol>
            </div>

            {/* Template Download Option */}
            <button
              onClick={downloadTemplate}
              className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-indigo-50/50 hover:bg-indigo-50 border border-dashed border-indigo-200 hover:border-indigo-400 rounded-xl transition-all group"
            >
              <FiDownload className="w-4 h-4 text-indigo-600 group-hover:scale-105 transition-transform" />
              <span className="text-xs font-bold text-indigo-600">Download Blank Sample Sheet</span>
            </button>

            {/* Interactive Upload Box area */}
            <div className="space-y-1.5">
              <label className="block text-xs font-bold tracking-wide text-slate-400 uppercase">
                Upload Filled Template
              </label>
              <div className="relative">
                <input
                  type="file"
                  accept=".xlsx,.xls"
                  onChange={handleFileSelect}
                  className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10"
                  disabled={loading}
                />
                <div className={`border-2 border-dashed rounded-xl p-6 text-center transition-all flex flex-col items-center justify-center ${
                  error && !file 
                    ? 'border-rose-200 bg-rose-50/40' 
                    : file 
                    ? 'border-indigo-300 bg-slate-50/50' 
                    : 'border-slate-200 hover:border-indigo-400 bg-white'
                }`}>
                  <div className={`w-10 h-10 rounded-xl border flex items-center justify-center mb-2.5 ${
                    error && !file 
                      ? 'bg-rose-50 border-rose-100 text-rose-500' 
                      : file 
                      ? 'bg-indigo-50 border-indigo-100 text-indigo-600' 
                      : 'bg-slate-50 border-slate-100 text-slate-400'
                  }`}>
                    <FiFile className="w-5 h-5" />
                  </div>
                  <p className="text-xs font-semibold text-slate-700 max-w-xs truncate px-2">
                    {file ? file.name : 'Click to select or drag Excel file here'}
                  </p>
                  {file && (
                    <p className="text-[10px] text-slate-400 mt-0.5 font-medium">
                      File Size: {(file.size / 1024).toFixed(1)} KB
                    </p>
                  )}
                  {error && !file && (
                    <p className="text-[11px] text-rose-600 font-medium mt-1">{error}</p>
                  )}
                </div>
              </div>
            </div>

            {/* Upload Progress Loader strip */}
            {loading && (
              <div className="p-3.5 bg-slate-50 rounded-xl border border-slate-100 space-y-2">
                <div className="flex justify-between items-center text-xs">
                  <span className="text-slate-500 font-medium">Sending data to server...</span>
                  <span className="font-bold text-indigo-600">{progress}%</span>
                </div>
                <div className="w-full bg-slate-200/80 rounded-full h-1.5 overflow-hidden">
                  <div
                    className="bg-indigo-600 h-full rounded-full transition-all duration-300"
                    style={{ width: `${progress}%` }}
                  />
                </div>
              </div>
            )}

            {/* Success Results layout block */}
            {result && (
              <div className="p-4 bg-emerald-50/60 rounded-xl border border-emerald-100 space-y-1">
                <div className="flex items-center gap-2 text-emerald-800">
                  <FiCheckCircle className="w-4 h-4 text-emerald-500 flex-shrink-0" />
                  <span className="text-xs font-bold">Successfully Uploaded</span>
                </div>
                <div className="text-xs text-emerald-700 font-medium pl-6 space-y-0.5">
                  <p>Added Rows: {result.created?.length || 0}</p>
                  {result.failed?.length > 0 && (
                    <p className="text-rose-600 font-bold">Failed Rows: {result.failed.length}</p>
                  )}
                </div>
              </div>
            )}

            {/* Dynamic Specific Validation Errors box */}
            {uploadErrors.length > 0 && (
              <div className="p-4 bg-amber-50/60 rounded-xl border border-amber-200/60 max-h-40 overflow-y-auto space-y-2">
                <div className="flex items-center gap-2 text-amber-800">
                  <FiAlertCircle className="w-4 h-4 text-amber-500 flex-shrink-0" />
                  <span className="text-xs font-bold">Sheet Formatting Errors</span>
                </div>
                <ul className="text-[11px] text-amber-700 space-y-1 list-disc list-inside pl-0.5 font-medium">
                  {uploadErrors.map((err, idx) => (
                    <li key={idx} className="leading-normal">{err}</li>
                  ))}
                </ul>
              </div>
            )}

            {/* General Server Failure Error text box */}
            {error && uploadErrors.length === 0 && (
              <div className="p-4 bg-rose-50/60 rounded-xl border border-rose-100 flex items-start gap-2.5">
                <FiAlertCircle className="w-4 h-4 text-rose-500 mt-0.5 flex-shrink-0" />
                <div>
                  <span className="text-xs font-bold text-rose-800 block">Upload Issue Found</span>
                  <p className="text-xs text-rose-600 font-medium mt-0.5 leading-normal">{error}</p>
                </div>
              </div>
            )}
          </div>

          {/* Action Footer Buttons bar */}
          <div className="px-5 py-4 bg-slate-50 border-t border-slate-100 flex gap-2 justify-end items-center flex-shrink-0">
            <button
              onClick={onClose}
              className="px-4 py-2 border border-slate-200 text-slate-600 bg-white hover:bg-slate-50 font-semibold text-xs rounded-lg transition-colors shadow-sm"
            >
              Cancel
            </button>
            <button
              onClick={handleUpload}
              disabled={!file || loading}
              className="flex items-center gap-1.5 px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white font-semibold text-xs rounded-lg transition-all disabled:opacity-40 disabled:cursor-not-allowed shadow-sm shadow-indigo-100"
            >
              <FiUpload className="w-3.5 h-3.5" />
              {loading ? 'Processing...' : 'Start Upload'}
            </button>
          </div>
        </motion.div>
      </AnimatePresence>
    </div>
  );
};

export default BulkUploadModal;